import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  BookOpen, Zap, Code2, FileJson, Beaker, HelpCircle,
  ChevronRight, ExternalLink, Home, BarChart3, Eye, Chrome, Download,
  Activity, Radar, ListChecks, MessageSquare, Bot, Store, Globe,
  Terminal, Cpu, Shield, Key, Webhook, Link2, Bell, Target,
  Gauge, Settings, Package, CheckSquare, Menu, X, ChevronDown,
  Truck, ShoppingCart, Database, Layers
} from 'lucide-react';

const sidebarItems = [
  {
    title: 'Welcome',
    items: [
      { title: 'Platform Overview', href: '/docs/welcome', icon: Home },
      { title: 'Getting Started', href: '/docs/getting-started', icon: Zap },
      { title: 'Onboarding Checklist', href: '/docs/onboarding-checklist', icon: CheckSquare },
    ]
  },
  {
    title: 'Shopify Guide',
    items: [
      { title: 'Shopify Setup', href: '/docs/shopify-setup', icon: Store },
      { title: 'Embedded App', href: '/docs/shopify-embedded', icon: Package },
      { title: 'Product & Inventory Sync', href: '/docs/shopify-sync', icon: Download },
    ]
  },
  {
    title: 'Non-Shopify Guide',
    items: [
      { title: 'Manual Setup', href: '/docs/manual-setup', icon: Settings },
      { title: 'API Integration', href: '/docs/api-integration', icon: Code2 },
      { title: 'Webhook Setup', href: '/docs/webhook-setup', icon: Webhook },
    ]
  },
  {
    title: 'Integrations — Marketplaces',
    items: [
      { title: 'Integration Map', href: '/docs/integrations', icon: Layers },
      { title: 'Amazon Seller Central', href: '/docs/integrations/marketplaces/amazon-seller', icon: ShoppingCart },
      { title: 'Amazon Vendor (EDI)', href: '/docs/integrations/marketplaces/amazon-vendor', icon: ShoppingCart },
      { title: 'Walmart Marketplace', href: '/docs/integrations/marketplaces/walmart', icon: ShoppingCart },
      { title: 'eBay', href: '/docs/integrations/marketplaces/ebay', icon: ShoppingCart },
      { title: 'Etsy', href: '/docs/integrations/marketplaces/etsy', icon: ShoppingCart },
      { title: 'Google Shopping', href: '/docs/integrations/marketplaces/google-shopping', icon: ShoppingCart },
      { title: 'Target Plus', href: '/docs/integrations/marketplaces/target-plus', icon: ShoppingCart },
    ]
  },
  {
    title: 'Integrations — Platforms',
    items: [
      { title: 'BigCommerce', href: '/docs/integrations/platforms/bigcommerce', icon: Store },
      { title: 'WooCommerce', href: '/docs/integrations/platforms/woocommerce', icon: Store },
      { title: 'Magento / Adobe Commerce', href: '/docs/integrations/platforms/magento', icon: Store },
      { title: 'Squarespace Commerce', href: '/docs/integrations/platforms/squarespace', icon: Store },
    ]
  },
  {
    title: 'Integrations — Warehousing & 3PL',
    items: [
      { title: '3PL Marketplace overview', href: '/docs/integrations/warehousing/wms-overview', icon: Truck },
      { title: 'ShipBob', href: '/docs/integrations/warehousing/shipbob', icon: Truck },
      { title: 'ShipMonk', href: '/docs/integrations/warehousing/shipmonk', icon: Truck },
      { title: 'ShipHero', href: '/docs/integrations/warehousing/shiphero', icon: Truck },
      { title: 'Amazon MCF', href: '/docs/integrations/warehousing/amazon-mcf', icon: Truck },
      { title: 'Flexport', href: '/docs/integrations/warehousing/flexport', icon: Truck },
      { title: 'Onboarding your 3PL', href: '/docs/integrations/warehousing/3pl-onboarding', icon: Truck },
    ]
  },
  {
    title: 'Integrations — OMS / ERP',
    items: [
      { title: 'NetSuite (SuiteTalk REST)', href: '/docs/integrations/oms/netsuite', icon: Database },
      { title: 'Manhattan Active Omni', href: '/docs/integrations/oms/manhattan', icon: Database },
    ]
  },
  {
    title: 'Messaging & OpenClaw',
    items: [
      { title: 'Channels & Signup', href: '/docs/channels-signup', icon: MessageSquare },
      { title: 'OpenClaw Overview', href: '/docs/openclaw', icon: MessageSquare },
      { title: 'Channel Setup', href: '/docs/channel-setup', icon: Globe },
      { title: 'Command Reference', href: '/docs/commands', icon: Terminal },
      { title: 'Account Linking', href: '/docs/account-linking', icon: Link2 },
      { title: 'Daily Briefs & Notifications', href: '/docs/daily-briefs', icon: Bell },
    ]
  },
  {
    title: 'AI Agents',
    items: [
      { title: 'The Agent Roster', href: '/docs/agent-roster', icon: ListChecks },
      { title: 'Agents Overview', href: '/docs/agents', icon: Bot },
      { title: 'Trust Ladder', href: '/docs/trust-ladder', icon: Gauge },
      { title: 'Guardrails & Limits', href: '/docs/guardrails', icon: Shield },
      { title: 'Agent Admin (Messaging)', href: '/docs/agent-admin', icon: Settings },
    ]
  },
  {
    title: 'Mac Mini Admin',
    items: [
      { title: 'Mac Mini Setup', href: '/docs/mac-mini', icon: Cpu },
      { title: 'Local LLM', href: '/docs/local-llm', icon: Bot },
    ]
  },
  {
    title: 'Promise Health',
    items: [
      { title: 'Promise Health Dashboard', href: '/docs/promise-health', icon: Activity },
      { title: 'Exception Radar', href: '/docs/exception-radar', icon: Radar },
      { title: 'Approval Queue', href: '/docs/approval-queue', icon: ListChecks },
      { title: 'Slack Integration', href: '/docs/slack-integration', icon: MessageSquare },
    ]
  },
  {
    title: 'Products',
    items: [
      { title: 'CI Proof Integration', href: '/docs/proof-integration', icon: Eye },
      { title: 'CI Shopper Guide', href: '/docs/shopper-guide', icon: Chrome },
    ]
  },
  {
    title: 'API & Integration',
    items: [
      { title: 'API Reference', href: '/docs/api-reference', icon: Key },
      { title: 'Events', href: '/docs/events', icon: FileJson },
      { title: 'JavaScript API', href: '/docs/js-api', icon: Code2 },
      { title: 'REST API', href: '/docs/rest-api', icon: FileJson },
    ]
  },
  {
    title: 'Signals',
    items: [
      { title: 'Signals Overview', href: '/docs/signals', icon: Zap },
      { title: 'CI Score', href: '/docs/ci-score', icon: BarChart3 },
    ]
  },
  {
    title: 'Resources',
    items: [
      { title: 'Examples & Recipes', href: '/docs/examples', icon: Beaker },
      { title: 'FAQ', href: '/docs/faq', icon: HelpCircle },
    ]
  }
];

export default function DocsLayout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Determine which section is active
  const activeSection = sidebarItems.find(section =>
    section.items.some(item => location.pathname === item.href)
  );

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#22304A] bg-[#0B1120]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
                  <span className="text-[#0B1120] font-bold text-sm">CI</span>
                </div>
                <span className="text-white font-semibold">Commerce Index</span>
              </Link>
              <span className="text-[#64748B]">/</span>
              <span className="text-[#3EF1C7] font-medium">Docs</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/proof"
                className="hidden sm:block text-sm text-[#94A3B8] hover:text-white transition-colors"
              >
                For Brands
              </Link>
              <Link
                to="/contact"
                className="hidden sm:block text-sm text-[#94A3B8] hover:text-white transition-colors flex items-center gap-1"
              >
                Support
              </Link>
              <button
                className="lg:hidden text-[#94A3B8]"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={cn(
            'w-64 py-8 flex-shrink-0',
            mobileOpen ? 'fixed inset-0 top-16 z-40 bg-[#0B1120] px-4 overflow-y-auto block' : 'hidden lg:block'
          )}>
            <nav className="sticky top-24 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto pb-8">
              {sidebarItems.map((section) => {
                const isActiveSection = activeSection?.title === section.title;
                return (
                  <div key={section.title}>
                    <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2 px-3">
                      {section.title}
                    </h4>
                    <ul className="space-y-0.5">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;
                        return (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={cn(
                                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                                isActive
                                  ? 'bg-[#3EF1C7]/10 text-[#3EF1C7] border border-[#3EF1C7]/20'
                                  : 'text-[#94A3B8] hover:text-white hover:bg-[#1E293B]'
                              )}
                            >
                              <Icon className="w-4 h-4" />
                              {item.title}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 py-8 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
